import { useNavigate } from "react-router-dom";
import { projects } from "../../../../database/projects";
import "./Projects.scss"
import ImageModal from "../../../../components/ImageModal";
import { Fragment, useState, useEffect, useRef } from "react";


// Generic single-element in-view hook
function useInView(ref: React.RefObject<Element>, options?: IntersectionObserverInit) {
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { threshold: 0.05, rootMargin: "0px 0px -40px 0px", ...options });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return inView;
}

// Observes each collage row independently — adds .row-visible when row enters viewport
function useCollageRowsInView(containerRef: React.RefObject<Element>) {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const rows = Array.from(container.querySelectorAll<HTMLElement>(".grid"));
        const observers: IntersectionObserver[] = [];

        rows.forEach((row) => {
            const obs = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    row.classList.add("row-visible");
                    obs.disconnect();
                }
            }, { threshold: 0.2, rootMargin: "0px 0px -40px 0px" });
            obs.observe(row);
            observers.push(obs);
        });

        return () => observers.forEach(o => o.disconnect());
    }, []);
}

// Each card slides in from left (even index) or right (odd index)
function useCardInView(containerRef: React.RefObject<Element>, delayPerItem = 80) {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const items = Array.from(container.querySelectorAll<HTMLElement>(".cs-anim"));
        const observers: IntersectionObserver[] = [];

        items.forEach((item, i) => {
            // Mark direction before observing
            item.classList.add(i % 2 === 0 ? "from-left" : "from-right");

            const obs = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    item.style.animationDelay = `${Math.floor(i / 2) * delayPerItem}ms`;
                    item.classList.add("slide-in");
                    obs.disconnect();
                }
            }, { threshold: 0.12, rootMargin: "0px 0px -20px 0px" });
            obs.observe(item);
            observers.push(obs);
        });

        return () => observers.forEach(o => o.disconnect());
    }, [delayPerItem]);
}


function Projects() {

    const [activeImage, setActiveImage] = useState<string | null>(null);
    const navigation = useNavigate();

    const headerRef: any = useRef<HTMLDivElement>(null);
    const collageRef: any = useRef<HTMLDivElement>(null);
    const csGridRef: any = useRef<HTMLDivElement>(null);

    const headerInView = useInView(headerRef, { threshold: 0.3 });
    useCollageRowsInView(collageRef);

    useCardInView(csGridRef, 80);

    const navigate = (link: string) => navigation(`project/${link}`);

    return (
        <Fragment>
            <section id="project" className="projects">
                <div className="background"></div>
                <div className="container">

                    {/* HEADER */}
                    <div
                        ref={headerRef}
                        className={`pj__header anim-header${headerInView ? " is-visible" : ""}`}
                    >
                        <span className="pj__eyebrow">My Work</span>
                        <h2 className="pj__title">Selected</h2>
                        <span className="pj__title-em">Projects & Work</span>
                    </div>

                    {/* COLLAGE */}
                    <div
                        ref={collageRef}
                        className="project-collage"
                    >
                        <div className="grid grid-5">
                            <div className="item col-span-2 collage-item from-left" style={{ "--delay": "0ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/musicplayer.webp")}>
                                <img src="/home/musicplayer.webp" alt="music-player" />
                            </div>
                            <div className="item col-span-3 collage-item from-right" style={{ "--delay": "80ms", maxHeight: "465px" } as React.CSSProperties} onClick={() => setActiveImage("/home/HomeHero.webp")}>
                                <img src="/home/HomeHero.webp" alt="jigsaw-hero" style={{ objectFit: "cover" }} />
                            </div>
                        </div>

                        <div className="grid grid-5">
                            <div className="item col-span-2 collage-item from-bottom-left" style={{ "--delay": "40ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/mural.webp")}>
                                <img src="/home/mural.webp" alt="mural" />
                            </div>
                            <div className="item col-span-3 collage-item from-top" style={{ "--delay": "130ms", maxHeight: "440px" } as React.CSSProperties} onClick={() => setActiveImage("/home/letterboxd.webp")}>
                                <img src="/home/letterboxd.webp" alt="letterboxd-redesign" />
                            </div>
                        </div>

                        <div className="grid grid-4">
                            <div className="item col-span-2 row-span-1 collage-item from-left" style={{ "--delay": "60ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/4.webp")}>
                                <img src="/home/4.webp" alt="binge-read" style={{ objectFit: "cover" }} />
                            </div>
                            <div className="item row-span-1 collage-item from-top" style={{ "--delay": "150ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/menu.webp")}>
                                <img src="/home/menu.webp" alt="sacred-berry-menu" />
                            </div>
                            <div className="item row-span-1 collage-item from-right" style={{ "--delay": "210ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/calendar.webp")}>
                                <img src="/home/calendar.webp" style={{ objectFit: "cover" }} />
                            </div>
                        </div>

                        <div className="grid grid-2">
                            <div className="item collage-item from-bottom-left" style={{ "--delay": "0ms" } as React.CSSProperties} onClick={() => setActiveImage("/home/5.webp")}>
                                <img src="/home/5.webp" alt="bakstak" />
                            </div>
                            <div className="item collage-item from-bottom-right" style={{ "--delay": "90ms", maxHeight: "272px" } as React.CSSProperties} onClick={() => setActiveImage("/home/coffee.webp")}>
                                <img src="/home/coffee.webp" alt="coffee" style={{ objectFit: "cover", objectPosition: "center 10%" }} />
                            </div>
                        </div>
                    </div>

                    {/* CASE STUDIES HEADER */}
                    <div className="pj__cs-header">
                        <span className="pj__cs-label">Case Studies</span>
                        <div className="pj__cs-line" />
                        <span className="pj__cs-label pj-num">{projects.length} Projects</span>
                    </div>

                    {/* CASE STUDY CARDS — no class toggle needed, JS drives inline styles */}
                    <div ref={csGridRef} className="pj__cs-grid">
                        {projects.map((project) => (
                            <div
                                key={project.name}
                                className="cs cs-anim"
                                onClick={() => navigate(project.link)}
                                role="button"
                                tabIndex={0}
                                aria-label={`View ${project.name} case study`}
                            >
                                <div className="cs__thumb">
                                    <img src={project.coverImage} alt={project?.name} />
                                </div>
                                <div className="cs__body">
                                    <div>
                                        <span className="cs__type">{project.type}</span>
                                        <div className="cs__title">{project.name}</div>
                                        <p className="cs__desc">{project.summary}</p>
                                    </div>
                                    <div className="cs__footer">
                                        <div className="cs__tags">
                                            {project.tags!.map(t => <span key={t} className="cs__tag">{t}</span>)}
                                        </div>
                                        <div className="cs__arrow">↗</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {activeImage && (
                <ImageModal
                    src={activeImage}
                    onClose={() => setActiveImage(null)}
                />
            )}
        </Fragment>
    )
}

export default Projects;